import { useEffect, useRef, useState } from "react";
import { Drawer, DrawerRef } from "../../../../../components/drawers/Drawer";
import { useHub } from "../../../../../core/Global";
import { API_ROUTES } from "../../../../../core/Constants";
import { toastError, toastSuccess } from "../../../../../components/base/Toast";
import Icon from "../../../../../components/base/Icon";
import FlexRow from "../../../../../components/base/FlexRow";
import TextArea from "../../../../../components/form/TextArea";
import Select from "../../../../../components/form/Select";
import Job from "../../models/Job";
import JobStatus from "../../../base/models/JobStatus";
import JobCancelReasonField, { CancelReasonValue, isCancelReasonValid } from "./JobCancelReasonField";

interface IProps {
    jobs: Job[]
    onClose: () => void
    onDone?: () => void
}

// Bulk status change for a set of selected jobs. Mirrors the single-job status
// update (POST jobstatus) against the new bulkstatus endpoint.
export default function JobsBulkStatusDrawer(props: IProps) {

    const hub = useHub()
    const drawer = useRef<DrawerRef>(null)
    const successRef = useRef<boolean>(false)

    const [statuses, statusesSet] = useState<JobStatus[]>([])
    const [selectedStatus, selectedStatusSet] = useState<JobStatus>()
    const [note, noteSet] = useState<string>("")
    const [working, workingSet] = useState<boolean>(false)
    // Only used when the chosen status is a cancellation.
    const [cancelReason, cancelReasonSet] = useState<CancelReasonValue>()

    useEffect(() => {
        // Never offer closing statuses in bulk (they need the per-job close flow),
        // matching the single-job status drawer.
        hub.Get(API_ROUTES.JobStatuses + "all", res => {
            if (res.success && res.data) statusesSet((res.data as JobStatus[]).filter(f => !f.isClosed && !f.isPendingClosed))
        })
    }, [])

    const count = props.jobs.length
    // Cancelling in bulk still has to say why, whether or not the status is note-required.
    const canceling = selectedStatus?.isCanceled ?? false

    const submit = () => {
        if (!selectedStatus?.id) {
            toastError("Please select a status.")
            return
        }
        if (canceling && !isCancelReasonValid(cancelReason)) {
            toastError("Please choose or write a cancel reason.")
            return
        }
        if (!canceling && selectedStatus.noteRequired && note.trim() === "") {
            toastError("A note is required for this status.")
            return
        }

        workingSet(true)
        hub.Post(API_ROUTES.Jobs + "bulkstatus", {
            ids: props.jobs.map(j => j.id),
            status: selectedStatus.id,
            note: canceling ? cancelReason?.note : note,
            cancelReasonId: canceling ? cancelReason?.cancelReasonId : undefined
        }, res => {
            workingSet(false)
            if (res.success) {
                const n = res.data ?? count
                toastSuccess(`Status updated for ${n} job${n === 1 ? "" : "s"}.`)
                // Close the drawer (runs its animation + cleanup); the selection is
                // cleared afterwards in onClose so the panel doesn't unmount abruptly.
                successRef.current = true
                drawer.current?.close()
            }
            else {
                toastError(res.message)
            }
        })
    }

    return (
        <Drawer ref={drawer} title="Change Status" titleIcon="arrow-right-arrow-left"
            subtitle={`${count} job${count === 1 ? "" : "s"} selected`}
            onClose={() => {
                if (successRef.current) props.onDone?.()
                props.onClose()
            }}>

            <div className="pt-2">

                {/* Status picker */}
                <div className="fs-8 fw-bold text-gray-400 text-uppercase mb-3">New Status</div>
                <div className="mb-6">
                    <Select id="bulk-statuses" name="status" block marginBottom={0}
                        source={statuses} value={selectedStatus?.id}
                        onSelectItem={st => { selectedStatusSet(st); noteSet("") }} />
                </div>

                {
                    canceling ?
                        <JobCancelReasonField onChange={cancelReasonSet} />
                        : selectedStatus?.noteRequired ?
                            <TextArea label="Status Note*" labelUp block name="bulk-status-note" rows={3}
                                value={note} onChange={e => noteSet(e.target.value)} />
                            : <></>
                }

                {/* Selected jobs */}
                <div className="fs-8 fw-bold text-gray-400 text-uppercase mt-6 mb-2">
                    Selected Jobs ({count})
                </div>
                <div className="scroll-y" style={{ maxHeight: 280 }}>
                    {
                        props.jobs.map((j, k) =>
                            <FlexRow key={k} className="py-2 border-bottom">
                                <span className="fw-bold fs-7 me-3 text-nowrap">#{j.jobId}</span>
                                <span className="fs-7 text-gray-700 text-truncate">{j.fullName}</span>
                                <span className="ms-auto d-flex align-items-center text-nowrap ps-2">
                                    <Icon icon="circle" type="solid" size={9} color={j.jobStatus?.color} className="me-1" />
                                    <span className="fs-8 text-gray-500">{j.jobStatus?.name}</span>
                                </span>
                            </FlexRow>
                        )
                    }
                </div>

                {/* Actions */}
                <FlexRow justify="end" className="border-top pt-4 mt-4">
                    <button type="button" className="btn btn-secondary btn-sm me-2" disabled={working}
                        onClick={() => drawer.current?.close()}>
                        Discard
                    </button>
                    <button type="button" className="btn btn-primary btn-sm" disabled={working || !selectedStatus}
                        onClick={submit}>
                        {
                            working ?
                                <><Icon icon="spinner-third" spin /><span className="ms-2">Please Wait</span></>
                                : <>Update {count} Job{count === 1 ? "" : "s"}</>
                        }
                    </button>
                </FlexRow>

            </div>
        </Drawer>
    )
}
